/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useEffect, useState} from 'react'
import {useIntl, defineMessages} from 'react-intl'
import {australiaStateMessages, australiaStateOptions} from './state-options-australia'
import {brazilStateMessages, brazilStateOptions} from './state-options-brazil'
import {caProvinceMessages, caProvinceOptions} from './province-options-ca'
import {mexicoStateMessages, mexicoStateOptions} from './state-options-mexico'
import {usStateMessages, usStateOptions} from './state-options-us'
import {
    COUNTRIES_USING_POSTAL_CODE,
    POSTAL_CODE_REGULAR_EXPRESSIONS,
    COUNTRIES_WITH_STATES_LIST
} from '../../pages/account/constant'
import {useToast} from '../../hooks/use-toast'
import useShipToCountry from '../../hooks/use-ship-to-country'
import {useXSSProtection} from './../../hooks/use-xss-protection'
import {sortShipToCountries} from '@app/utils/ship-to-country-utils'

const messages = defineMessages({
    required: {defaultMessage: 'Required', id: 'use_address_fields.error.required'},
    fullAddress: {
        defaultMessage: 'Enter your full address',
        id: 'use_address_fields.label.full_address'
    },
    firstName: {defaultMessage: 'First Name', id: 'use_address_fields.label.first_name'},
    lastName: {defaultMessage: 'Last Name', id: 'use_address_fields.label.last_name'},
    phone: {defaultMessage: 'Phone', id: 'use_address_fields.label.phone'},
    country: {defaultMessage: 'Country', id: 'use_address_fields.label.country'},
    address: {defaultMessage: 'Address Line 1', id: 'use_address_fields.label.address'},
    address2: {defaultMessage: 'Address Line 2', id: 'use_address_fields.label.address2'},
    address2Placeholder: {
        defaultMessage: 'Apt, building or floor',
        id: 'use_address_fields.placeholder.address2'
    },
    city: {defaultMessage: 'City', id: 'use_address_fields.label.city'},
    state: {defaultMessage: 'State', id: 'use_address_fields.label.state'},
    province: {defaultMessage: 'Province', id: 'use_address_fields.label.province'},
    zipCode: {defaultMessage: 'Zip Code', id: 'use_address_fields.label.zipCode'},
    postalCode: {defaultMessage: 'Postal Code', id: 'use_address_fields.label.postal_code'},
    stateCodeInvalid: {
        defaultMessage: 'Please enter 2-letter state/province.',
        id: 'use_address_fields.error.state_code_invalid'
    },
    preferred: {defaultMessage: 'Set as default', id: 'use_address_fields.label.preferred'},
    fullNameLengthLimit: {
        defaultMessage:
            'The length for your firstname + lastname is limited to 30 characters maximum.',
        id: 'use_address_fields.error.full_name_length_limit'
    },
    noAmpersand: {
        defaultMessage: 'The "&" character is not allowed',
        id: 'use_address_fields.error.noAmpersand'
    }
})

/**
 * Validates that the combined length of a first and last name, including a space between them,
 * does not exceed 30 characters.
 *
 * @param {string} firstName - The first name to validate.
 * @param {string} lastName - The last name to validate.
 * @returns {boolean} True if the total length of the full name (first name, space, last name) is less than or equal to 30 characters
 */
function validateFullNameLengthLimit(firstName, lastName) {
    const fullNameLength = firstName.length + lastName.length + 1 // +1 for the space

    return fullNameLength <= 30
}

/**
 * A React hook that provides the field definitions for an address form.
 * @param {Object} form - The object returned from `useForm`
 * @param {Object} form.control - The form control object
 * @param {Object} form.formState.errors - An object containing field errors
 * @returns {Object} Field definitions for use in a form
 */
export default function useAddressFields({
    form: {
        watch,
        setValue,
        control,
        clearErrors,
        setError,
        trigger,
        formState: {errors}
    },
    prefix = '',
    forcePostalCodeValidation = false
}) {
    const toast = useToast()
    const {formatMessage} = useIntl()
    const {shipToCountry} = useShipToCountry()

    const countryCode = watch(`${prefix}countryCode`)
    const phoneCountryCode = watch(`${prefix}c_phoneCountry`)
    const phone = watch(`${prefix}phone`)

    const defaultCountryCode = countryCode || shipToCountry?.id

    const [defaultPhoneCountry, setDefaultPhoneCountry] = useState(defaultCountryCode)
    const [stateRules, setStateRules] = useState({})
    const [postalCodeRules, setPostalCodeRules] = useState({})
    const countryList = sortShipToCountries({formatMessage}).map((country) => ({
        value: country.code,
        label: country.name
    }))

    // Sync phone country code on empty phone field
    useEffect(() => {
        if (phone === '') {
            setDefaultPhoneCountry(countryCode)
        } else {
            setDefaultPhoneCountry(phoneCountryCode || defaultCountryCode)
        }

        const newStateRules = COUNTRIES_WITH_STATES_LIST.includes(countryCode)
            ? {
                  required:
                      countryCode === 'CA'
                          ? 'Please select your province.' // FYI we won't translate this
                          : formatMessage({
                                defaultMessage: 'Please select your state.',
                                id: 'use_address_fields.error.please_select_your_state_or_province',
                                description:
                                    'Error message for a blank state (US-specific checkout)'
                            })
              }
            : {}
        setStateRules(newStateRules)

        if (COUNTRIES_USING_POSTAL_CODE.includes(countryCode)) {
            const newPostalCodeRules = {
                required:
                    countryCode === 'CA'
                        ? 'Please enter your postal code.' // FYI we won't translate this
                        : formatMessage({
                              defaultMessage: 'Please enter your zip code.',
                              id: 'use_address_fields.error.please_enter_your_postal_or_zip',
                              description:
                                  'Error message for a blank zip code (US-specific checkout)'
                          })
            }
            const postalCodeRegex = POSTAL_CODE_REGULAR_EXPRESSIONS[countryCode]
            if (postalCodeRegex) {
                newPostalCodeRules.pattern = {
                    value: postalCodeRegex,
                    message:
                        countryCode === 'CA'
                            ? 'Please enter a valid postal code.' // FYI we won't translate this
                            : formatMessage({
                                  defaultMessage: 'Please enter a valid zip code.',
                                  id: 'use_address_fields.error.invalid_postal_or_zip_code'
                              })
                }
            } else {
                newPostalCodeRules.pattern = {}
            }
            setPostalCodeRules(newPostalCodeRules)
        } else {
            setPostalCodeRules({})
        }
    }, [countryCode])

    // Use case: force postalCode validation on billing country change
    useEffect(() => {
        if (forcePostalCodeValidation) {
            trigger(`${prefix}postalCode`)
            trigger(`${prefix}stateCode`)
        }
    }, [postalCodeRules, stateRules])

    const {validateField} = useXSSProtection()

    const localizedAUStateOptions = australiaStateOptions.map((option) => ({
        ...option,
        label: formatMessage(australiaStateMessages[option.label])
    }))

    const localizedBRStateOptions = brazilStateOptions.map((option) => ({
        ...option,
        label: formatMessage(brazilStateMessages[option.label])
    }))

    const localizedCAProvinceOptions = caProvinceOptions.map((option) => ({
        ...option,
        label: formatMessage(caProvinceMessages[option.label])
    }))

    const localizedMXStateOptions = mexicoStateOptions.map((option) => ({
        ...option,
        label: formatMessage(mexicoStateMessages[option.label])
    }))

    const localizedUSStateOptions = usStateOptions.map((option) => ({
        ...option,
        label: formatMessage(usStateMessages[option.label])
    }))

    const localizedStateOptionsMap = {
        CA: localizedCAProvinceOptions,
        BR: localizedBRStateOptions,
        MX: localizedMXStateOptions,
        AU: localizedAUStateOptions,
        US: localizedUSStateOptions
    }

    const getStateLocalizedOptions = (countryCode) => {
        return localizedStateOptionsMap[countryCode] || []
    }

    const isNoAmpersand = (value) => !value.includes('&')
    const noAmpersandMessage = formatMessage(messages.noAmpersand)
    const noAmpersandValidator = (value) => isNoAmpersand(value) || noAmpersandMessage

    const validateFullName = (isLastNameValidation) => {
        const firstName = watch(`${prefix}firstName`)
        const isFirstNameNoAmpersand = isNoAmpersand(firstName)

        // stop full name validation on ampersand invalid case for the first name
        if (!isFirstNameNoAmpersand) {
            return true
        }

        const lastName = watch(`${prefix}lastName`)
        const isLastNameNoAmpersand = isNoAmpersand(lastName)
        const isFullNameValid = validateFullNameLengthLimit(firstName, lastName)

        // on firstName validation
        if (!isLastNameValidation) {
            if (isLastNameNoAmpersand) {
                if (isFullNameValid) {
                    // remove invalid state - red outline
                    clearErrors([`${prefix}lastName`])
                } else {
                    setError(`${prefix}lastName`, {type: 'manual'})
                }
            }
        }

        // on lastName validation
        if (isLastNameValidation) {
            if (isFullNameValid) {
                // remove invalid state - red outline
                clearErrors([`${prefix}firstName`])
            } else {
                // Mark firstName as invalid with error message
                setError(`${prefix}firstName`, {
                    type: 'manual',
                    message: formatMessage(messages.fullNameLengthLimit)
                })
            }
        }

        return isFullNameValid
    }

    const fields = {
        c_fullAddress: {
            name: `${prefix}c_fullAddress`,
            label: formatMessage(messages.fullAddress),
            defaultValue: '',
            type: 'text',
            autoComplete: 'full-address',
            rules: {
                validate: (value) =>
                    validateField(
                        toast,
                        value,
                        'address-form',
                        'full-address',
                        formatMessage(messages.fullAddress)
                    )
            },
            error: errors[`${prefix}c_fullAddress`],
            control
        },
        firstName: {
            name: `${prefix}firstName`,
            label: formatMessage(messages.firstName),
            defaultValue: '',
            type: 'text',
            autoComplete: 'given-name',
            inputProps: {maxLength: 20},
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your first name.',
                    id: 'use_address_fields.error.please_enter_first_name'
                }),
                // not pure validation (side effects: toast, dependency on last name field)
                // it should be implemented as simple function in the rules object
                validate: (value) => {
                    // Ampersand validation
                    if (!isNoAmpersand(value)) return noAmpersandMessage

                    // Full name validation
                    const isFullNameValid = validateFullName()
                    if (!isFullNameValid) {
                        return formatMessage(messages.fullNameLengthLimit)
                    }

                    // XSS validation: + validateXssForm() to block form submit
                    validateField(
                        toast,
                        value,
                        'address-form',
                        'firstname',
                        formatMessage(messages.firstName)
                    )
                }
            },
            error: errors[`${prefix}firstName`],
            control
        },
        lastName: {
            name: `${prefix}lastName`,
            label: formatMessage(messages.lastName),
            defaultValue: '',
            type: 'text',
            autoComplete: 'family-name',
            inputProps: {maxLength: 20},
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your last name.',
                    id: 'use_address_fields.error.please_enter_last_name'
                }),
                validate: (value) => {
                    // Ampersand validation
                    if (!isNoAmpersand(value)) return noAmpersandMessage

                    // Full name validation
                    const isFullNameValid = validateFullName(true)
                    if (!isFullNameValid) {
                        return ''
                    }

                    // XSS validation: + validateXssForm() to block form submit
                    validateField(
                        toast,
                        value,
                        'address-form',
                        'lastname',
                        formatMessage(messages.lastName)
                    )
                }
            },
            error: errors[`${prefix}lastName`],
            control
        },
        phone: {
            name: `${prefix}phone`,
            label: formatMessage(messages.phone),
            defaultValue: '',
            defaultCountry: defaultPhoneCountry,
            type: 'tel',
            autoComplete: 'tel',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your phone number.',
                    id: 'use_address_fields.error.please_enter_phone_number'
                }),
                validate: (value) =>
                    validateField(
                        toast,
                        value,
                        'address-form',
                        'phone',
                        formatMessage(messages.phone)
                    )
            },
            error: errors[`${prefix}phone`],
            trigger,
            control,
            setValue, // pass setValue to update dial select on defaultCountry update
            clearErrors
        },
        countryCode: {
            name: `${prefix}countryCode`,
            label: formatMessage(messages.country),
            defaultValue: defaultCountryCode,
            type: 'select',
            options: countryList,
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please select your country.',
                    id: 'use_address_fields.error.please_select_your_country'
                })
            },
            error: errors[`${prefix}countryCode`],
            control
        },
        address1: {
            name: `${prefix}address1`,
            label: formatMessage(messages.address),
            defaultValue: '',
            type: 'text',
            autoComplete: 'address-line1',
            inputProps: {maxLength: 35},
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your address.',
                    id: 'use_address_fields.error.please_select_your_address'
                }),
                validate: {
                    noAmpersand: noAmpersandValidator,
                    isValidField: (value) =>
                        validateField(
                            toast,
                            value,
                            'address-form',
                            'address-line1',
                            formatMessage(messages.address)
                        )
                }
            },
            error: errors[`${prefix}address1`],
            control
        },
        address2: {
            name: `${prefix}address2`,
            label: formatMessage(messages.address2),
            placeholder: formatMessage(messages.address2Placeholder),
            defaultValue: '',
            type: 'text',
            autoComplete: 'address-line2',
            inputProps: {maxLength: 35},
            rules: {
                validate: {
                    noAmpersand: noAmpersandValidator,
                    isValidField: (value) =>
                        validateField(
                            toast,
                            value,
                            'address-form',
                            'address-line2',
                            formatMessage(messages.address2)
                        )
                }
            },
            error: errors[`${prefix}address2`],
            control
        },
        city: {
            name: `${prefix}city`,
            label: formatMessage(messages.city),
            defaultValue: '',
            type: 'text',
            inputProps: {maxLength: 35},
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter your city.',
                    id: 'use_address_fields.error.please_select_your_city'
                }),
                validate: {
                    noAmpersand: noAmpersandValidator,
                    isValidField: (value) =>
                        validateField(
                            toast,
                            value,
                            'address-form',
                            'city',
                            formatMessage(messages.city)
                        )
                }
            },
            error: errors[`${prefix}city`],
            control
        },
        stateCode: {
            name: `${prefix}stateCode`,
            label: formatMessage(countryCode === 'CA' ? messages.province : messages.state),
            defaultValue: '',
            inputProps: {maxLength: 35},
            type: COUNTRIES_WITH_STATES_LIST.includes(countryCode) ? 'select' : 'text',
            options: [{value: '', label: ''}, ...getStateLocalizedOptions(countryCode)],
            rules: {
                ...stateRules,
                validate: {
                    noAmpersand: noAmpersandValidator
                }
            },
            error: errors[`${prefix}stateCode`],
            control
        },
        postalCode: {
            name: `${prefix}postalCode`,
            label: formatMessage(countryCode === 'CA' ? messages.postalCode : messages.zipCode),
            defaultValue: '',
            type: 'text',
            autoComplete: 'postal-code',
            inputProps: {maxLength: 35},
            rules: {
                ...postalCodeRules,
                validate: {
                    noAmpersand: noAmpersandValidator,
                    isValidField: (value) => validateField(toast, value, 'address-form', 'zipcode')
                }
            },
            error: errors[`${prefix}postalCode`],
            control
        },
        preferred: {
            name: `${prefix}preferred`,
            label: formatMessage(messages.preferred),
            defaultValue: false,
            type: 'checkbox',
            autoComplete: 'honorific-prefix',
            rules: {},
            control
        }
    }

    return fields
}
